import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Drawer } from '@/design-system/primitives';
import { useClinicalPatientSummary } from '@/lib/api/clinicalAdminHooks';

import type { ClinicalAgentContext } from './ClinicalAgentDrawer';
import {
  BillingTab,
  DocumentsTab,
  EmrTab,
  IntakeTab,
  OrdersTab,
  OverviewTab,
  PrescriptionsTab,
} from './CustomerTabs';
import { ErrorState, LoadingState } from './ui';

type PreviewTab =
  | 'overview'
  | 'emr'
  | 'prescriptions'
  | 'orders'
  | 'billing'
  | 'intake'
  | 'documents';

const TABS: { key: PreviewTab; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'emr', label: 'EMR' },
  { key: 'prescriptions', label: 'Prescriptions' },
  { key: 'orders', label: 'Orders' },
  { key: 'billing', label: 'Billing' },
  { key: 'intake', label: 'Intake' },
  { key: 'documents', label: 'Documents' },
];

/** In-place CRM preview so board/queue position is never lost. */
export function PatientPreviewDrawer({
  patientId,
  onClose,
  onOpenTicket,
  onOpenAgent,
}: {
  patientId: string | null;
  onClose: () => void;
  onOpenTicket?: (ticketId: string) => void;
  onOpenAgent?: (context: ClinicalAgentContext) => void;
}) {
  const [tab, setTab] = useState<PreviewTab>('overview');
  const patient = useClinicalPatientSummary(patientId ?? '', Boolean(patientId));

  useEffect(() => setTab('overview'), [patientId]);

  const summary = patient.data;
  return (
    <Drawer
      open={Boolean(patientId)}
      onClose={onClose}
      title={summary?.display_name || 'Patient preview'}
      size="wide"
      layer="nested"
    >
      <div className="p-15 flex flex-col gap-16">
        {patient.isLoading ? <LoadingState label="Loading patient…" /> : null}
        {patient.isError ? <ErrorState>Could not load this patient.</ErrorState> : null}
        {summary && patientId ? (
          <>
            <div className="flex flex-wrap items-start gap-10">
              <div className="min-w-0">
                <h2 className="type-heading-lg">{summary.display_name || '(no name)'}</h2>
                <p className="type-label-sm text-ink-muted">{summary.email || '—'}</p>
              </div>
              <div className="ml-auto flex flex-wrap gap-8">
                {onOpenAgent ? (
                  <button
                    type="button"
                    onClick={() =>
                      onOpenAgent({
                        patientName: summary.display_name,
                        patientEmail: summary.email,
                      })
                    }
                    className="type-label-sm px-10 py-7 rounded border border-accent text-accent bg-accent-wash"
                  >
                    Clinical AI
                  </button>
                ) : null}
                <Link
                  to={`/admin/clinical-admin/customers/${patientId}`}
                  className="type-label-sm px-10 py-7 rounded border border-line hover:bg-sunken"
                >
                  Open full profile ↗
                </Link>
              </div>
            </div>

            <div className="flex gap-2 border-b border-line overflow-x-auto">
              {TABS.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setTab(item.key)}
                  className={`type-label-sm whitespace-nowrap px-10 py-9 -mb-px border-b-2 ${
                    tab === item.key
                      ? 'border-ink text-ink'
                      : 'border-transparent text-ink-muted hover:text-ink'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            {tab === 'overview' && (
              <OverviewTab summary={summary} onOpenTicket={onOpenTicket} />
            )}
            {tab === 'emr' && <EmrTab patientId={patientId} />}
            {tab === 'prescriptions' && <PrescriptionsTab patientId={patientId} />}
            {tab === 'orders' && <OrdersTab patientId={patientId} />}
            {tab === 'billing' && <BillingTab patientId={patientId} />}
            {tab === 'intake' && <IntakeTab patientId={patientId} />}
            {tab === 'documents' && <DocumentsTab patientId={patientId} />}
          </>
        ) : null}
      </div>
    </Drawer>
  );
}
